import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import './FilterFieldRenderer.css';

const CheckboxOptionList = ({ options = [], value, isMulti, onChange, placeholder = "Search options...", allowUnselect = true }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((opt) => {
      if (typeof opt === 'object' && opt !== null) {
        return { label: opt.label ?? opt.name ?? String(opt.value), value: opt.value };
      }
      return { label: String(opt), value: opt };
    });
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return normalizedOptions;
    const term = searchTerm.toLowerCase().trim();
    return normalizedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(term) || String(opt.value).toLowerCase().includes(term)
    );
  }, [normalizedOptions, searchTerm]);

  const handleToggle = (optValue) => {
    if (isMulti) {
      const currentArr = Array.isArray(value) ? value : [];
      const exists = currentArr.some((v) => String(v) === String(optValue));
      if (exists) {
        onChange(currentArr.filter((v) => String(v) !== String(optValue)));
      } else {
        onChange([...currentArr, optValue]);
      }
    } else {
      const isCurrent = String(value) === String(optValue);
      // Fields marked allowUnselect: false must always hold a value - clicking
      // the current selection is a no-op rather than clearing it.
      if (isCurrent && !allowUnselect) return;
      onChange(isCurrent ? '' : optValue);
    }
  };

  const isChecked = (optValue) => {
    if (isMulti) {
      return Array.isArray(value) && value.some((v) => String(v) === String(optValue));
    }
    return value !== undefined && value !== null && value !== '' && String(value) === String(optValue);
  };

  return (
    <div className="checkboxFilterContainer">
      {normalizedOptions.length > 5 && (
        <div className="filterSearchBox" style={{ marginBottom: '12px' }}>
          <FontAwesomeIcon icon={faSearch} className="searchIcon" />
          <input
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button type="button" className="clearInputBtn" onClick={() => setSearchTerm('')}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      )}

      <div className="checkboxFilterList">
        {filteredOptions.length === 0 ? (
          <div className="noFilterResults">No options found</div>
        ) : (
          filteredOptions.map((opt) => {
            const selected = isChecked(opt.value);
            return (
              // The toggle lives on the input's onChange, not a manual onClick
              // + preventDefault on the label. Clicking the label text relies on
              // the browser's native "label forwards its click to the control it
              // wraps" behavior to reach the input; clicking the checkbox square
              // reaches the input directly. A label-level onClick only ever sees
              // the first of those and had to preventDefault the browser's own
              // checkbox toggle to stay in sync - which held for a label-text
              // click but left the checkbox's own native toggle racing the
              // preventDefault when the input was clicked directly, so the
              // visual state could disagree with React's. onChange is the one
              // event both paths funnel through, so both stay in sync.
              <label
                key={String(opt.value)}
                className={`checkboxFilterItem ${selected ? 'selected' : ''}`}
              >
                <input
                  type="checkbox"
                  className="checkboxFilterInput"
                  checked={selected}
                  onChange={() => handleToggle(opt.value)}
                />
                <span className="checkboxFilterLabel">{opt.label}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

const FilterFieldRenderer = ({ field, value, onChange, filters = {} }) => {
  // Helper functions
  const getMinDate = () => {
    switch (field.key) {
      case 'created_to':
        return filters.created_from || '';

      case 'loan_creation_to':
        return filters.loan_creation_from || '';

      // case 'from_date':
      //   return filters.to_date ? '' : '';

      // case 'from_loan_date':
      //   return filters.to_loan_date ? '' : '';

      case 'to_date':
        return filters.from_date || '';

      case 'to_loan_date':
        return filters.from_loan_date || '';

      case 'loan_created_to':
        return filters.loan_created_from || '';

      // case 'loan_creation_from':
      //   return filters.loan_creation_to || '';

      case 'completion_end_date':
        return filters.completion_start_date || '';

      case 'creation_end_date':
        return filters.creation_start_date || '';

      default:
        return '';
    }
  };

  const getMaxDate = () => {
    switch (field.key) {
      case 'created_from':
        return filters.created_to || '';

      case 'loan_creation_from':
        return filters.loan_creation_to || '';

      case 'from_date':
        return filters.to_date || '';

      case 'from_loan_date':
        return filters.to_loan_date || '';

      case 'loan_created_from':
        return filters.loan_created_to || '';

      case 'completion_start_date':
        return filters.completion_end_date || '';

      case 'creation_start_date':
        return filters.creation_end_date || '';

      default:
        return '';
    }
  };

  switch (field.type) {
    case 'text':
      return (
        <div className={`filterSearchBox ${field?.hideSearchIcon ? 'filterSearchBox--noIcon' : ''}`}>
          {/* This component doubles as a plain form-field renderer outside
              filter panels (e.g. TVRSubmissionModal), where a search icon and
              a "Search by ..." placeholder don't make sense - a field can opt
              out with hideSearchIcon, which defaults to showing it so every
              existing filter search box is unaffected. The modifier class
              reclaims the input's left padding, which every .filterSearchBox
              input rule reserves for the icon regardless of whether it renders. */}
          {!field?.hideSearchIcon && (
            <FontAwesomeIcon icon={faSearch} className="searchIcon" />
          )}
          <input
            type="text"
            className={`form-control ${field?.isReadOnly ? 'readonly-input' : ''}`}
            placeholder={field.placeholder || (field?.hideSearchIcon ? `Enter ${field.label}` : `Search by ${field.label}`)}
            value={value || ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            readOnly={field?.isReadOnly}
            maxLength={field.maxCharacters}
            autoFocus
          />
          {value && (
            <button
              type="button"
              className="clearInputBtn"
              onClick={() => onChange(field.key, '')}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      );

  case 'date_range': {
      const startKey = field?.startKey || 'start_date';
      const endKey = field?.endKey || 'end_date';
      return (
        <div className="datePickerContainer">
          <div className="datePickerField">
            <label>Start Date</label>
            <input
              type="date"
              className="form-control date-picker-filter"
              value={filters?.[startKey] || ''}
              onChange={(e) => onChange(startKey, e.target.value)}
            />
          </div>
          <div className="datePickerField">
            <label>End Date</label>
            <input
              type="date"
              className="form-control date-picker-filter"
              value={filters?.[endKey] || ''}
              min={filters?.[startKey] || ''}
              onChange={(e) => onChange(endKey, e.target.value)}
            />
          </div>
        </div>
      );
    }

    case 'date': {
      let inputType = field?.format === "YYYY-MM" ? 'month' : 'date';
      const isMonthPicker = field?.format === "YYYY-MM";

      const selectedDate = value ? isMonthPicker
                                    ? new Date(`${value}-01`)
                                    : new Date(value)
                                  : null;

      const minDate = getMinDate()
                        ? new Date(getMinDate())
                        : null;

      const maxDate = getMaxDate()
                      ? new Date(getMaxDate())
                      : null;

      return (
        // <DatePicker
        //   selected={selectedDate}
        //   onChange={(date) => {

        //     if (!date) {
        //       onChange(field.key, '');
        //       return;
        //     }

        //     let formattedValue;

        //     // MONTH FORMAT
        //     if (isMonthPicker) {

        //       const year = date.getFullYear();

        //       const month = String(
        //         date.getMonth() + 1
        //       ).padStart(2, '0');

        //       formattedValue =
        //         `${year}-${month}`;

        //     }

        //     // DATE FORMAT
        //     else {

        //       const year = date.getFullYear();

        //       const month = String(
        //         date.getMonth() + 1
        //       ).padStart(2, '0');

        //       const day = String(
        //         date.getDate()
        //       ).padStart(2, '0');

        //       formattedValue =
        //         `${year}-${month}-${day}`;
        //     }

        //     onChange(field.key, formattedValue);
        //   }}

        //   dateFormat={
        //     isMonthPicker
        //       ? "yyyy-MM"
        //       : "yyyy-MM-dd"
        //   }

        //   showMonthYearPicker={isMonthPicker}

        //   placeholderText={
        //     isMonthPicker
        //       ? "YYYY-MM"
        //       : "YYYY-MM-DD"
        //   }

        //   minDate={minDate}
        //   maxDate={maxDate}

        //   className="form-control"

        //   isClearable
        // />

        <input
          // type="date"
          type={inputType}
          className={`form-control ${field?.isReadOnly ? 'readonly-input' : ''}`}
          min={getMinDate()}
          max={getMaxDate()}
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
        />
      );
    }

    case 'select':
      if (field?.options && field.options.length > 0) {
        return (
          <CheckboxOptionList
            options={field.options}
            value={value}
            isMulti={false}
            onChange={(newVal) => onChange(field.key, newVal)}
            placeholder={`Search ${field.label}...`}
            allowUnselect={field?.allowUnselect !== false}
          />
        );
      }
      return (
        <select
          className={`form-select ${field?.isReadOnly ? 'readonly-input' : ''}`}
          id={field.key}
          name={field.key}
          value={value === true ? 'true' : value === false ? 'false' : value || ''}
          onChange={(e) => {
            let selectedValue = e.target.value;
            if (selectedValue === 'true') selectedValue = true;
            if (selectedValue === 'false') selectedValue = false;
            onChange(field.key, selectedValue);
          }}
        >
          {field?.allowUnselect !== false && (
            <option value="">Select {field.label}</option>
          )}
          {field?.options?.map(opt => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    
    case 'multiselect':
      if (field?.options && field.options.length > 0) {
        return (
          <CheckboxOptionList
            options={field.options}
            value={value}
            isMulti={true}
            onChange={(newVal) => onChange(field.key, newVal)}
            placeholder={`Search ${field.label}...`}
          />
        );
      }
      return (
        <div className="multiselect-wrapper">
          <select
            className={`form-select ${field?.isReadOnly ? 'readonly-input' : ''}`}
            multiple
            id={field.key}
            name={field.key}
            value={Array.isArray(value) ? value : []}
            onChange={(e) => {
              const selectedValues = Array.from(
                e.target.selectedOptions,
                option => option.value
              );
              onChange(field.key, selectedValues);
            }}
          >
            {field?.options?.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      );

    case 'number':
      return (
        <input
          type='number'
          className={`form-control ${field?.isReadOnly ? 'readonly-input' : ''}`}
          placeholder={field.placeholder || field.label}
          value={value ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            onChange(
              field.key,
              val === '' ? '' : Number(val)
            );

            // onChange(field.key, Number(e.target.value))
          }}
          readOnly={field?.isReadOnly}
        />
      );

    default:
      return null;
  }
};

export default FilterFieldRenderer;